import { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useCategoriesRedux } from '../../hooks/useCategoriesRedux'
import { useIsMobile } from '../../hooks/useIsMobile'
import { setLoginPopupOpen } from '../../store/slices/uiSlice'
import {
  closeWishlistModal,
  openWishlistModal,
  selectWishlistModalIsOpen,
} from '../../store/slices/wishlistModalSlice'
import { RootState } from '../../store/store'
import Container from '../Container/Container'
import { MobileMenu } from '../MobileMenu/MobileMenu'
import B2BButton from './B2BButton'
import CartIcon from './CartIcon'
import styles from './Header.module.css'
import { AboutIcon, ContactIcon, VacancyIcon } from './Icons'
import SearchIcon from './SearchIcon'
import UserIcon from './UserIcon'
import WishlistIcon from './WishlistIcon'

const LoginPopup = lazy(() => import('../LoginPopup/LoginPopup'))
const SearchPopup = lazy(() => import('../SearchPopup/SearchPopup'))
const WishlistModal = lazy(() => import('../WishlistModal/WishlistModal'))

const NAV_ITEMS = [
  { id: '/contacts', label: 'Клієнтам', Icon: ContactIcon },
  { id: '/maps', label: "Кав'ярні", Icon: AboutIcon },
  { id: '/vacancies', label: 'Актуальні вакансії', Icon: VacancyIcon },
]

const Header = () => {
  const dispatch = useDispatch()
  const { categories, loading: categoriesLoading } = useCategoriesRedux()
  const { isLoginPopupOpen } = useSelector((state: RootState) => state.ui)
  const navigate = useNavigate()
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSticky, setIsSticky] = useState(false)
  const [isHiding, setIsHiding] = useState(false)
  const [lastScrollY, setLastScrollY] = useState(0)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const isWishlistModalOpen = useSelector(selectWishlistModalIsOpen)

  // Заборона скролу body при відкритому меню
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMenuOpen])

  // Функція для навігації з прокручуванням вгору
  const navigateWithScroll = useCallback(
    (path: string) => {
      navigate(path)
      // Прокручуємо вгору після навігації
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        })
      }, 100)
    },
    [navigate],
  )

  // Навігація на сторінки
  const handleNavClick = useCallback(
    (id: string) => {
      navigateWithScroll(id)
      setIsMenuOpen(false)
    },
    [navigateWithScroll],
  )

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      if (currentScrollY > 80 && currentScrollY < lastScrollY) {
        setIsSticky(true)
        setIsHiding(false)
      } else if (currentScrollY > lastScrollY) {
        if (isSticky && !isHiding && currentScrollY > 80) {
          setIsHiding(true)
        }
      }
      setLastScrollY(currentScrollY)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY, isSticky, isHiding])

  const handleAnimationEnd = () => {
    if (isHiding) {
      setIsSticky(false)
      setIsHiding(false)
    }
  }

  const handleSearchClick = () => {
    setIsSearchOpen(true)
  }

  const handleCloseSearch = () => {
    setIsSearchOpen(false)
  }

  const handleCloseLoginPopup = () => {
    dispatch(setLoginPopupOpen(false))
  }

  const handleOpenWishlistModal = () => {
    dispatch(openWishlistModal())
  }

  const handleCloseWishlistModal = () => {
    dispatch(closeWishlistModal())
  }

  // Закриваємо мобільне меню при відкритті попапу логіна
  useEffect(() => {
    if (isLoginPopupOpen && isMenuOpen) {
      setIsMenuOpen(false)
    }
  }, [isLoginPopupOpen, isMenuOpen])

  const isMobile = useIsMobile()

  return (
    <div className={`${styles.stickyWrap}`} onAnimationEnd={handleAnimationEnd}>
      <Container>
        <header className={`${styles.header} ${isMenuOpen ? styles.menuOpen : ''}`}>
              <div className={styles.left}>
                <button
                  className={styles.toggleMenu}
                  onClick={() => setIsMenuOpen(v => !v)}
                  aria-label={isMenuOpen ? 'Закрити меню' : 'Відкрити меню'}
                >
                  {!isMenuOpen && (
                    <svg
                      className={styles.openMenuIcon}
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <rect y="3" width="12" height="2" rx="1" fill="#017223" />
                      <rect y="9" width="20" height="2" rx="1" fill="#017223" />
                      <rect y="15" width="16" height="2" rx="1" fill="#017223" />
                    </svg>
                  )}
                  {isMenuOpen && (
                    <svg
                      className={styles.closeMenuIcon}
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="3.63623"
                        y="2.22182"
                        width="20"
                        height="2"
                        rx="1"
                        transform="rotate(45 3.63623 2.22182)"
                        fill="#017223"
                      />
                      <rect
                        width="20"
                        height="2"
                        rx="1"
                        transform="matrix(-0.707107 0.707107 0.707107 0.707107 16.3643 2.22182)"
                        fill="#017223"
                      />
                    </svg>
                  )}
                </button>
                  <SearchIcon
                    onSearchClick={handleSearchClick}
                    onCloseSearch={handleCloseSearch}
                    isSearchOpen={isSearchOpen}
                    isMobile={isMobile}
                  />
              </div>
            <MobileMenu
              open={isMenuOpen}
              onClose={() => setIsMenuOpen(false)}
              onNavClick={handleNavClick}
              navItemCategories={categories.map(category => ({
                id: category.internalName,
                label: category.externalName,
                Icon: () => <img src={category.iconUrl} alt="" width={16} height={16} />,
              }))}
              navItems={NAV_ITEMS}
            />
          <div className={styles.leftCol}>
            <Link
              to="/"
              className={styles.logo}
              aria-label="Seaters Coffee — на головну"
              onClick={() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
              }}
            >
              <svg
                width="83"
                height="71"
                viewBox="0 0 83 71"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M16.7405 3.87923C14.8046 4.29747 13.2126 5.85353 12.7381 7.79135C12.5902 8.39538 12.5797 8.52078 12.4577 11.1455C12.3425 13.6216 12.3035 14.0809 12.1067 15.2758C11.7739 17.2967 11.0518 19.5711 10.1724 21.3691C10.0219 21.6769 7.73807 25.9704 5.09737 30.9101C2.45667 35.8499 0.237622 40.039 0.16619 40.2193C0.00522482 40.6256 -0.0477251 41.2824 0.046903 41.6994C0.231011 42.5112 0.777642 43.2338 1.45689 43.5637C1.65065 43.6578 3.12004 44.1299 4.72227 44.6129L7.63536 45.491V45.891C7.63542 48.267 7.91946 64.7099 7.96607 65.0327C8.30697 67.3942 10.1288 69.248 12.4318 69.5768C12.9759 69.6544 58.0611 71.0407 58.689 70.9991C59.2985 70.9587 59.904 70.7913 60.5237 70.4919C61.1736 70.1779 61.602 69.8629 62.0811 69.347C62.5661 68.8248 62.7601 68.5353 63.0519 67.8989C63.457 67.0156 63.4997 66.7408 63.6487 64.0637C63.7364 62.4889 63.81 61.5697 63.8655 61.3556C63.9633 60.9784 64.2332 60.5217 64.4877 60.3026C64.9017 59.9463 65.0953 59.8912 68.0317 59.2956C71.5985 58.572 72.4569 58.3094 74.1561 57.4219C78.3844 55.2133 81.4668 51.1042 82.4198 46.4058C82.6987 45.0306 82.7418 44.3883 82.8916 39.3648C83.0087 35.4395 83.0227 34.4802 82.9727 33.7843C82.7637 30.8745 81.9024 28.2928 80.34 25.8927C79.697 24.905 79.2637 24.369 78.3656 23.4503C77.2519 22.311 76.3242 21.5823 74.9844 20.7944C73.8848 20.1476 73.1094 19.8408 69.4466 18.6036C67.2232 17.8525 65.8335 17.355 65.6799 17.2552C65.3531 17.0429 65.1004 16.7373 64.9133 16.3282C64.7612 15.9957 64.7532 15.9282 64.4905 12.7497C64.1944 9.16794 64.163 8.97002 63.7533 8.09904C63.0755 6.65842 61.8579 5.61281 60.3946 5.21463C60.1435 5.14627 59.6337 5.07365 59.1768 5.0411C58.3257 4.98045 18.6359 3.76791 17.7431 3.77522C17.445 3.77772 16.9938 3.82451 16.7405 3.87923ZM64.7722 26.8112C64.4644 26.9681 64.228 27.2141 64.0413 27.5714L63.9191 27.8054L63.905 38.5864C63.8892 50.6268 63.8599 49.7122 64.2771 50.1971C64.6186 50.594 64.9645 50.7557 65.4637 50.7522C65.8632 50.7494 65.9288 50.726 69.3117 49.378C71.2045 48.6237 72.9053 47.9198 73.0912 47.8137C74.4163 47.0577 75.3537 45.7338 75.6789 44.1593C75.7748 43.695 76.0558 34.9428 76.0162 33.6544C75.9882 32.7416 75.8592 32.187 75.5095 31.4763C74.9086 30.2547 73.8926 29.2972 72.7125 28.8401C72.4666 28.7449 70.8087 28.2174 69.0283 27.6679C65.3871 26.5442 65.3218 26.5311 64.7722 26.8112Z"
                  fill="#017223"
                />
                <path
                  d="M22.7188 43.2826L19.8747 43.4577C19.8261 43.2146 19.7215 42.9958 19.5611 42.8013C19.4007 42.602 19.1892 42.444 18.9266 42.3273C18.669 42.2058 18.3603 42.145 18.0005 42.145C17.5192 42.145 17.1132 42.2471 16.7826 42.4513C16.452 42.6506 16.2868 42.918 16.2868 43.2535C16.2868 43.5209 16.3937 43.7469 16.6076 43.9317C16.8215 44.1164 17.1886 44.2647 17.7088 44.3765L19.7361 44.7849C20.8251 45.0085 21.637 45.3683 22.1718 45.8642C22.7066 46.3601 22.974 47.0115 22.974 47.8186C22.974 48.5527 22.7577 49.1969 22.325 49.7511C21.8971 50.3053 21.3089 50.738 20.5602 51.0492C19.8163 51.3555 18.9582 51.5086 17.9859 51.5086C16.5031 51.5086 15.3217 51.1999 14.4417 50.5825C13.5666 49.9602 13.0537 49.1142 12.903 48.0447L15.9586 47.8842C16.051 48.3364 16.2746 48.6815 16.6295 48.9198C16.9844 49.1531 17.439 49.2698 17.9932 49.2698C18.5377 49.2698 18.9753 49.1653 19.3059 48.9562C19.6413 48.7423 19.8115 48.4676 19.8163 48.1322C19.8115 47.8502 19.6924 47.6193 19.459 47.4394C19.2256 47.2546 18.8659 47.1136 18.3797 47.0164L16.4399 46.6299C15.346 46.4111 14.5317 46.0319 13.9969 45.4923C13.467 44.9526 13.202 44.2647 13.202 43.4285C13.202 42.709 13.3965 42.0891 13.7854 41.5689C14.1792 41.0487 14.731 40.6476 15.4408 40.3656C16.1555 40.0836 16.9917 39.9427 17.9494 39.9427C19.3642 39.9427 20.4775 40.2417 21.2894 40.8396C22.1062 41.4376 22.5826 42.252 22.7188 43.2826ZM28.5975 51.5086C27.4453 51.5086 26.4535 51.2752 25.6221 50.8085C24.7957 50.3369 24.1588 49.6709 23.7115 48.8104C23.2642 47.945 23.0406 46.9216 23.0406 45.7402C23.0406 44.588 23.2642 43.5768 23.7115 42.7065C24.1588 41.8363 24.7884 41.1581 25.6003 40.6719C26.417 40.1857 27.3748 39.9427 28.4735 39.9427C29.2125 39.9427 29.9004 40.0618 30.5373 40.3C31.179 40.5334 31.7381 40.8858 32.2146 41.3574C32.6959 41.829 33.0702 42.4221 33.3376 43.1368C33.605 43.8466 33.7387 44.6779 33.7387 45.6308V46.4841H24.2803V44.5588H30.8144C30.8144 44.1116 30.7172 43.7153 30.5227 43.3701C30.3283 43.025 30.0584 42.7551 29.7132 42.5607C29.3729 42.3613 28.9767 42.2617 28.5246 42.2617C28.053 42.2617 27.6349 42.3711 27.2702 42.5898C26.9105 42.8038 26.6285 43.093 26.4243 43.4577C26.2201 43.8174 26.1156 44.2185 26.1107 44.6609V46.4913C26.1107 47.0456 26.2128 47.5245 26.417 47.928C26.6261 48.3315 26.9202 48.6426 27.2994 48.8614C27.6786 49.0802 28.1283 49.1896 28.6485 49.1896C28.9937 49.1896 29.3097 49.141 29.5966 49.0437C29.8834 48.9465 30.1289 48.8006 30.3331 48.6062C30.5373 48.4117 30.6929 48.1735 30.7998 47.8915L33.6731 48.0811C33.5272 48.7715 33.2282 49.3743 32.7761 49.8897C32.3288 50.4001 31.7503 50.7988 31.0405 51.0856C30.3355 51.3676 29.5212 51.5086 28.5975 51.5086ZM37.5227 51.5013C36.808 51.5013 36.1711 51.3773 35.612 51.1294C35.0529 50.8766 34.6105 50.5047 34.2848 50.0136C33.9639 49.5177 33.8035 48.9003 33.8035 48.1613C33.8035 47.539 33.9177 47.0164 34.1462 46.5934C34.3747 46.1705 34.6859 45.8302 35.0797 45.5725C35.4735 45.3148 35.9207 45.1204 36.4215 44.9891C36.9271 44.8578 37.457 44.7655 38.0113 44.712C38.6627 44.6439 39.1878 44.5807 39.5865 44.5224C39.9851 44.4592 40.2744 44.3668 40.4543 44.2452C40.6341 44.1237 40.7241 43.9438 40.7241 43.7056V43.6618C40.7241 43.2 40.5782 42.8427 40.2865 42.5898C39.9997 42.337 39.5913 42.2106 39.0614 42.2106C38.5023 42.2106 38.0575 42.3346 37.7269 42.5826C37.3963 42.8256 37.1775 43.1319 37.0705 43.5014L34.1973 43.2681C34.3431 42.5874 34.63 41.9992 35.0578 41.5033C35.4856 41.0025 36.0374 40.6184 36.7132 40.351C37.3938 40.0788 38.1814 39.9427 39.076 39.9427C39.6983 39.9427 40.2938 40.0156 40.8626 40.1614C41.4363 40.3073 41.9444 40.5334 42.3868 40.8396C42.8341 41.1459 43.1865 41.5397 43.4442 42.021C43.7019 42.4975 43.8307 43.0687 43.8307 43.7348V51.2898H40.8845V49.7365H40.797C40.6171 50.0866 40.3765 50.3953 40.0751 50.6627C39.7736 50.9252 39.4114 51.1318 38.9885 51.2825C38.5655 51.4284 38.0769 51.5013 37.5227 51.5013ZM38.4124 49.3573C38.8694 49.3573 39.2729 49.2674 39.6229 49.0875C39.973 48.9027 40.2476 48.6548 40.447 48.3437C40.6463 48.0325 40.746 47.68 40.746 47.2862V46.0976C40.6487 46.1608 40.515 46.2191 40.3449 46.2726C40.1796 46.3212 39.9924 46.3674 39.7834 46.4111C39.5743 46.45 39.3652 46.4865 39.1562 46.5205C38.9471 46.5497 38.7575 46.5764 38.5874 46.6007C38.2228 46.6542 37.9043 46.7393 37.6321 46.856C37.3598 46.9727 37.1483 47.1307 36.9976 47.33C36.8469 47.5245 36.7715 47.7675 36.7715 48.0592C36.7715 48.4822 36.9247 48.8055 37.231 49.0291C37.5421 49.2479 37.9359 49.3573 38.4124 49.3573ZM50.7614 40.0885V42.4221H44.0158V40.0885H50.7614ZM45.5472 37.4049H48.6538V47.8478C48.6538 48.1346 48.6976 48.3582 48.7851 48.5187C48.8726 48.6742 48.9942 48.7836 49.1497 48.8468C49.3102 48.91 49.4949 48.9416 49.704 48.9416C49.8498 48.9416 49.9957 48.9295 50.1415 48.9052C50.2874 48.876 50.3992 48.8541 50.477 48.8395L50.9656 51.1513C50.81 51.1999 50.5912 51.2558 50.3092 51.319C50.0273 51.3871 49.6845 51.4284 49.281 51.443C48.5323 51.4721 47.876 51.3725 47.312 51.144C46.7529 50.9155 46.3178 50.5606 46.0067 50.0793C45.6955 49.598 45.5424 48.9903 45.5472 48.2561V37.4049ZM20.3779 57.9244V60.3893H13.5666V57.9244H20.3779ZM26.6923 65.5086C25.5401 65.5086 24.5483 65.2752 23.717 64.8085C22.8905 64.3369 22.2536 63.6709 21.8063 62.8104C21.359 61.945 21.1354 60.9216 21.1354 59.7402C21.1354 58.588 21.359 57.5768 21.8063 56.7065C22.2536 55.8363 22.8832 55.1581 23.6951 54.6719C24.5118 54.1857 25.4696 53.9427 26.5683 53.9427C27.3073 53.9427 27.9952 54.0618 28.6321 54.3C29.2739 54.5334 29.833 54.8858 30.3094 55.3574C30.7907 55.829 31.1651 56.4221 31.4325 57.1368C31.6999 57.8466 31.8335 58.6779 31.8335 59.6308V60.4841H22.3751V58.5588H28.9092C28.9092 58.1116 28.812 57.7153 28.6175 57.3701C28.4231 57.025 28.1533 56.7551 27.8081 56.5607C27.4678 56.3613 27.0715 56.2617 26.6194 56.2617C26.1478 56.2617 25.7297 56.3711 25.3651 56.5898C25.0053 56.8038 24.7233 57.093 24.5191 57.4577C24.315 57.8174 24.2104 58.2185 24.2056 58.6609V60.4913C24.2056 61.0456 24.3077 61.5245 24.5118 61.928C24.7209 62.3315 25.015 62.6426 25.3942 62.8614C25.7735 63.0802 26.2232 63.1896 26.7434 63.1896C27.0885 63.1896 27.4046 63.141 27.6914 63.0437C27.9782 62.9465 28.2237 62.8006 28.4279 62.6062C28.6321 62.4117 28.7877 62.1735 28.8947 61.8915L31.7679 62.0811C31.6221 62.7715 31.3231 63.3743 30.8709 63.8897C30.4237 64.4001 29.8451 64.7988 29.1353 65.0856C28.4304 65.3676 27.616 65.5086 26.6923 65.5086ZM32.4234 65.2898V54.0885H35.4352V56.0429H35.5519C35.7561 55.3477 36.0988 54.8226 36.5801 54.4677C37.0614 54.108 37.6157 53.9281 38.2428 53.9281C38.3984 53.9281 38.5661 53.9378 38.746 53.9572C38.9259 53.9767 39.0839 54.0034 39.22 54.0375V56.794C39.0742 56.7503 38.8724 56.7114 38.6147 56.6774C38.3571 56.6433 38.1213 56.6263 37.9074 56.6263C37.4504 56.6263 37.042 56.726 36.6822 56.9253C36.3273 57.1198 36.0453 57.392 35.8363 57.7421C35.6321 58.0921 35.53 58.4956 35.53 58.9526V65.2898H32.4234ZM48.7085 57.2826L45.8644 57.4577C45.8158 57.2146 45.7113 56.9958 45.5509 56.8013C45.3904 56.602 45.179 56.444 44.9164 56.3273C44.6588 56.2058 44.35 56.145 43.9903 56.145C43.509 56.145 43.103 56.2471 42.7724 56.4513C42.4418 56.6506 42.2765 56.918 42.2765 57.2535C42.2765 57.5209 42.3835 57.7469 42.5974 57.9317C42.8113 58.1164 43.1784 58.2647 43.6986 58.3765L45.7259 58.7849C46.8149 59.0085 47.6268 59.3683 48.1616 59.8642C48.6964 60.3601 48.9638 61.0115 48.9638 61.8186C48.9638 62.5527 48.7474 63.1969 48.3147 63.7511C47.8869 64.3053 47.2986 64.738 46.5499 65.0492C45.8061 65.3555 44.948 65.5086 43.9757 65.5086C42.4929 65.5086 41.3115 65.1999 40.4315 64.5825C39.5564 63.9602 39.0435 63.1142 38.8928 62.0447L41.9484 61.8842C42.0407 62.3364 42.2644 62.6815 42.6193 62.9198C42.9742 63.1531 43.4287 63.2698 43.983 63.2698C44.5275 63.2698 44.965 63.1653 45.2956 62.9562C45.6311 62.7423 45.8012 62.4676 45.8061 62.1322C45.8012 61.8502 45.6821 61.6193 45.4488 61.4394C45.2154 61.2546 44.8556 61.1136 44.3695 61.0164L42.4297 60.6299C41.3358 60.4111 40.5215 60.0319 39.9867 59.4923C39.4567 58.9526 39.1918 58.2647 39.1918 57.4285C39.1918 56.709 39.3863 56.0891 39.7752 55.5689C40.169 55.0487 40.7208 54.6476 41.4306 54.3656C42.1453 54.0836 42.9815 53.9427 43.9392 53.9427C45.354 53.9427 46.4673 54.2417 47.2792 54.8396C48.096 55.4376 48.5724 56.252 48.7085 57.2826Z"
                  fill="#FEFEFE"
                />
                <path
                  d="M68.5705 2.65468C68.5705 2.40427 68.6316 2.176 68.7537 1.96987C68.8759 1.76373 69.04 1.59959 69.2461 1.47744C69.4523 1.35529 69.6805 1.29421 69.931 1.29421C70.1356 1.29421 70.3241 1.3362 70.4967 1.42018C70.6692 1.50416 70.8135 1.61944 70.9295 1.76603C71.0471 1.91108 71.1258 2.07675 71.1655 2.26303H70.7394C70.6936 2.0966 70.5959 1.96071 70.4463 1.85535C70.2966 1.74999 70.1249 1.69731 69.931 1.69731C69.7554 1.69731 69.595 1.74083 69.45 1.82786C69.3049 1.91337 69.1889 2.02865 69.1018 2.17371C69.0163 2.31724 68.9736 2.47756 68.9736 2.65468C68.9736 2.83027 69.0163 2.9906 69.1018 3.13565C69.1889 3.28071 69.3049 3.39675 69.45 3.48379C69.595 3.56929 69.7554 3.61205 69.931 3.61205C70.1249 3.61205 70.2913 3.55937 70.4302 3.45401C70.5692 3.34866 70.6623 3.21276 70.7097 3.04633H71.1334C71.0952 3.23261 71.0204 3.39905 70.9089 3.54563C70.7975 3.69068 70.6578 3.8052 70.4898 3.88918C70.3218 3.97316 70.1356 4.01515 69.931 4.01515C69.6805 4.01515 69.4523 3.95407 69.2461 3.83192C69.04 3.70977 68.8759 3.54563 68.7537 3.3395C68.6316 3.13336 68.5705 2.90509 68.5705 2.65468ZM69.8943 5.07329C69.5614 5.07329 69.2484 5.01069 68.9553 4.88548C68.6636 4.76028 68.4063 4.58697 68.1834 4.36557C67.962 4.14265 67.7879 3.88536 67.6612 3.59372C67.536 3.30209 67.4742 2.98907 67.4757 2.65468C67.4772 2.32029 67.5406 2.00728 67.6658 1.71564C67.7925 1.424 67.9666 1.16748 68.188 0.94608C68.4094 0.723152 68.6659 0.549086 68.9576 0.42388C69.2492 0.298674 69.5614 0.236071 69.8943 0.236071C70.2287 0.236071 70.5417 0.298674 70.8334 0.42388C71.1265 0.549086 71.383 0.723152 71.6029 0.94608C71.8243 1.16748 71.9976 1.424 72.1228 1.71564C72.248 2.00728 72.3114 2.32029 72.3129 2.65468C72.3144 2.98907 72.2526 3.30209 72.1274 3.59372C72.0022 3.88536 71.8289 4.14265 71.6075 4.36557C71.3861 4.58697 71.1288 4.76028 70.8356 4.88548C70.5425 5.01069 70.2287 5.07329 69.8943 5.07329ZM69.8943 4.67019C70.1737 4.67019 70.4356 4.61827 70.6799 4.51445C70.9242 4.40909 71.138 4.2648 71.3212 4.08157C71.506 3.89681 71.6502 3.68229 71.7541 3.43798C71.8594 3.19368 71.9113 2.93334 71.9098 2.65697C71.9083 2.37755 71.8548 2.11569 71.7495 1.87138C71.6457 1.62708 71.5014 1.41331 71.3166 1.23008C71.1334 1.04533 70.9204 0.901036 70.6776 0.797207C70.4348 0.691851 70.1737 0.639173 69.8943 0.639173C69.6164 0.639173 69.3561 0.691851 69.1133 0.797207C68.8705 0.901036 68.6568 1.04533 68.472 1.23008C68.2888 1.41484 68.1445 1.62937 68.0391 1.87367C67.9338 2.11645 67.8803 2.37755 67.8788 2.65697C67.8773 2.93334 67.9284 3.19368 68.0323 3.43798C68.1376 3.68076 68.2827 3.89452 68.4674 4.07928C68.6522 4.26251 68.8667 4.4068 69.111 4.51216C69.3553 4.61751 69.6164 4.67019 69.8943 4.67019Z"
                  fill="#017223"
                />
              </svg>
            </Link>
            <nav className={styles.navWrapper}>
              <ul className={styles.nav}>
                {/* Категорії товарів */}
                {!categoriesLoading &&
                  categories.map(category => {
                    return (
                      <li
                        key={category.internalName}
                        className={
                          location.pathname === `/shop/${category.internalName}`
                            ? styles.active
                            : ''
                        }
                      >
                        <Link
                          to={`/shop/${category.internalName}`}
                          onClick={() => {
                            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
                          }}
                        >
                        {category.iconUrl.startsWith('data:image/svg+xml') ||
                        category.iconUrl.startsWith('<svg') ? (
                          category.iconUrl.startsWith('data:image/svg+xml') ? (
                            <img
                              src={category.iconUrl}
                              alt={category.externalName}
                              className={styles.icon}
                              width={16}
                              height={16}
                            />
                          ) : (
                            <div
                              className={styles.icon}
                              dangerouslySetInnerHTML={{
                                __html: category.iconUrl,
                              }}
                            />
                          )
                        ) : (
                          <img
                            src={category.iconUrl}
                            alt={category.externalName}
                            className={styles.icon}
                            width={16}
                            height={16}
                            onError={e => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                            }}
                          />
                        )}
                        {category.externalName}
                        </Link>
                      </li>
                    )
                  })}

                {/* Інші пункти меню */}
                {NAV_ITEMS.map(({ id, label, Icon }) => (
                  <li
                    key={id}
                    className={location.pathname === id ? styles.active : ''}
                  >
                    <Link
                      to={id}
                      onClick={() => {
                        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
                      }}
                    >
                      <Icon className={styles.icon} />
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
          <div className={styles.rightGroup}>
            <B2BButton />
            <div className={styles.actions}>
              <div className={styles.desktopSearch}>
                <SearchIcon
                  onSearchClick={handleSearchClick}
                  onCloseSearch={handleCloseSearch}
                  isSearchOpen={isSearchOpen}
                  isMobile={false}
                />
              </div>
              {!isMenuOpen && <WishlistIcon onClick={handleOpenWishlistModal} />}

              {!isMenuOpen && <CartIcon />}

              <UserIcon />
            </div>
          </div>
        </header>
      </Container>
      <Suspense fallback={null}>
        {isSearchOpen && <SearchPopup isOpen={isSearchOpen} onClose={handleCloseSearch} />}
        {isLoginPopupOpen && (
          <LoginPopup isOpen={isLoginPopupOpen} onClose={handleCloseLoginPopup} />
        )}
        {isWishlistModalOpen && (
          <WishlistModal isOpen={isWishlistModalOpen} onClose={handleCloseWishlistModal} />
        )}
      </Suspense>
    </div>
  )
}

export default Header
